import type { LocationSuggestion } from '@ixigo/cab-core';
import type { SearchFormData } from '@/components/search-form';

export interface ReviewSearchParams extends Required<SearchFormData> {
	cabId: string;
}

/**
 * Parses SearchFormData from URL search parameters for the review page
 * @param searchParams - Record of search parameters from URL
 * @returns SearchFormData if valid, null if invalid or missing
 */
export function parseReviewSearchParams(
	searchParams: Record<string, unknown>,
): ReviewSearchParams | null {
	try {
		const { pickup, drop, dateTime, cabId } = searchParams;

		if (
			!pickup ||
			!drop ||
			!dateTime ||
			!cabId ||
			typeof pickup !== 'string' ||
			typeof drop !== 'string' ||
			typeof dateTime !== 'string' ||
			typeof cabId !== 'string' ||
			!isValidISODateString(dateTime)
		) {
			return null;
		}

		const cleanPickup =
			pickup.startsWith('"') && pickup.endsWith('"')
				? pickup.slice(1, -1)
				: pickup;

		const cleanDrop =
			drop.startsWith('"') && drop.endsWith('"') ? drop.slice(1, -1) : drop;

		let parsedPickup: LocationSuggestion;
		let parsedDrop: LocationSuggestion;

		try {
			parsedPickup = JSON.parse(cleanPickup);
			parsedDrop = JSON.parse(cleanDrop);
		} catch {
			return null;
		}

		const isValidPickup = isValidLocationSuggestion(parsedPickup);
		const isValidDrop = isValidLocationSuggestion(parsedDrop);
		if (!isValidPickup || !isValidDrop) {
			return null;
		}

		return {
			pickup: parsedPickup,
			drop: parsedDrop,
			dateTime,
			cabId,
		};
	} catch {
		return null;
	}
}

/**
 * Validates if a string is a valid ISO date string
 * @param dateString - String to validate
 * @returns true if valid ISO date string, false otherwise
 */
function isValidISODateString(dateString: string): boolean {
	if (!dateString) return false;

	const date = new Date(dateString);
	return !Number.isNaN(date.getTime()) && date.toISOString() === dateString;
}

/**
 * Validates if an object is a valid LocationSuggestion
 * @param obj - Object to validate
 * @returns true if valid LocationSuggestion, false otherwise
 */
function isValidLocationSuggestion(obj: any): obj is LocationSuggestion {
	return (
		obj &&
		typeof obj === 'object' &&
		obj !== null &&
		(typeof obj.placeId === 'string' ||
			(typeof obj.lat === 'number' && typeof obj.lng === 'number'))
	);
}
